import { ApiError } from '../lib/api.ts';

interface ApiErrorBody {
  error?: string;
  details?: { message?: string; constraint?: string; detail?: string; [k: string]: unknown };
  [k: string]: unknown;
}

// HTTP status → 白話訊息. 後端如果有給 details.message 中文 (e.g. zod
// validation_error 的具體欄位提示), 那條優先; 沒有時用這條 fallback。
const STATUS_FALLBACK: Record<number, string> = {
  400: '輸入的資料有問題，請檢查欄位內容。',
  401: '尚未登入或登入逾期，請重新登入。',
  403: '你沒有權限做這個動作。',
  404: '找不到資料（可能已刪除或無權限）。',
  409: '跟現有資料衝突（例如編碼重複、同一筆已存在），請確認後再試。',
  422: '輸入的資料有問題，請檢查欄位內容。',
  429: '操作太頻繁，請稍候再試。',
  500: '系統內部錯誤，請稍候再試或聯絡資訊主管。',
  502: '系統暫時無回應，請稍候再試。',
  503: '系統暫時無回應，請稍候再試。',
  504: '系統回應太慢，請稍候再試。',
};

// body.error 字串 → 白話訊息. 跟 status 互補, 通常後端兩個都會給。
const ERROR_CODE_FALLBACK: Record<string, string> = {
  forbidden: '你沒有權限做這個動作。',
  unauthenticated: '尚未登入或登入逾期，請重新登入。',
  not_found: '找不到資料。',
  validation_error: '輸入的資料有問題，請檢查欄位內容。',
  bad_request: '輸入的資料有問題，請檢查欄位內容。',
  conflict: '跟現有資料衝突，請確認後再試。',
  internal_error: '系統內部錯誤，請稍候再試或聯絡資訊主管。',
  rate_limited: '操作太頻繁，請稍候再試。',
};

function friendlyMessage(status: number, body: ApiErrorBody | undefined): string {
  // 1) 後端明確給的 details.message 最優先 (e.g. zod 報「name_zh: 不可空」)
  const explicit = body?.details?.message;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;

  // 2) 用 error code 對白話 dict
  const code = body?.error;
  if (code && ERROR_CODE_FALLBACK[code]) return ERROR_CODE_FALLBACK[code];

  // 3) 用 HTTP status 對白話 dict
  if (STATUS_FALLBACK[status]) return STATUS_FALLBACK[status];

  // 4) 最後 fallback
  return '操作失敗，請稍候再試。';
}

export function ErrorBlock({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    const body = error.body as ApiErrorBody | undefined;
    const friendly = friendlyMessage(error.status, body);
    const debugLine = [
      `[${error.status}]`,
      body?.error,
      body?.details?.constraint ? `· ${body.details.constraint}` : null,
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded">
        <div className="font-medium">{friendly}</div>
        <div className="text-[11px] text-red-500 mt-1 font-mono">{debugLine}</div>
      </div>
    );
  }
  // 非 ApiError (e.g. network failure, JS Error)
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded">
      <div className="font-medium">操作失敗。</div>
      <div className="text-[11px] text-red-500 mt-1 font-mono break-all">{message}</div>
    </div>
  );
}
